/**
 * Admin layer configuration.
 *
 * The admin layer sits on top of the existing retreX application. It reuses the
 * existing user model (`userDetail` shape returned by `user/userLogin`), the
 * existing role names (`primaryUserType`) and the existing feature/menu
 * structure (`constants/menu.config`).
 *
 * NOTE: the admin layer currently runs on STATIC data (see
 * `data/admin/admin.static.data.js`). Every backend capability that does not
 * exist yet is listed in `ADMIN_API_GAPS` below instead of inventing an API
 * contract. See `modules/admin/README.md`.
 */

export const ADMIN_BASE_PATH = '/admin';

/**
 * The application has ONE login screen. Admins sign in through the common
 * retreX login page (`component/Login/LoginForm`), which resolves a static
 * admin address locally and pushes straight to `ADMIN_ROUTES.dashboard`.
 * There is deliberately no `/admin/login` route.
 */
export const COMMON_LOGIN_ROUTE = '/login';

export const ADMIN_ROUTES = {
  dashboard: `${ADMIN_BASE_PATH}/dashboard`,
  users: `${ADMIN_BASE_PATH}/users`,
  roles: `${ADMIN_BASE_PATH}/roles`,
  agencies: `${ADMIN_BASE_PATH}/agencies`,
  featureSets: `${ADMIN_BASE_PATH}/feature-sets`,
  analytics: `${ADMIN_BASE_PATH}/analytics`,
  profile: `${ADMIN_BASE_PATH}/profile`,
  contactInformation: `${ADMIN_BASE_PATH}/contact-information`,
  auditLog: `${ADMIN_BASE_PATH}/audit-log`,
  reports: `${ADMIN_BASE_PATH}/reports`,
};

/**
 * Storage keys are deliberately namespaced so the admin session never collides
 * with the end-user session created by `component/Login/LoginForm`.
 */
export const ADMIN_STORAGE_KEYS = {
  isLogin: 'isAdminLogin',
  session: 'adminDetail',
  data: 'adminWorkspaceData',
};

/** Granular capabilities used to gate admin navigation and admin actions. */
export const ADMIN_PERMISSIONS = {
  DASHBOARD_VIEW: 'admin.dashboard.view',
  USERS_VIEW: 'admin.users.view',
  USERS_MANAGE: 'admin.users.manage',
  ROLES_ASSIGN: 'admin.roles.assign',
  AGENCIES_VIEW: 'admin.agencies.view',
  FEATURES_MANAGE: 'admin.features.manage',
  ANALYTICS_VIEW: 'admin.analytics.view',
  /**
   * Ticket Management is parked — the module is not in the navigation and has
   * no route. The permission is kept so restoring the module (see
   * `_archived-modules/admin/README.md`) is a menu entry and a route again,
   * not a permission migration across every role.
   */
  TICKETS_MANAGE: 'admin.tickets.manage',
  PROFILE_VIEW: 'admin.profile.view',
  CONTACT_MANAGE: 'admin.contact.manage',
  ACTIVITY_VIEW: 'admin.activity.view',
  /**
   * Gates "Send password reset" on a user row. There is no Password Management
   * page any more — resets are sent from User Management, which is the only
   * place an admin ever needed them (see `_archived-modules/admin/README.md`).
   */
  PASSWORD_RESET: 'admin.password.reset',
  REPORTS_VIEW: 'admin.reports.view',
};

export const ADMIN_ROLES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  SUPPORT: 'Support',
};

export const ADMIN_ROLE_PERMISSIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: Object.values(ADMIN_PERMISSIONS),
  [ADMIN_ROLES.ADMIN]: [
    ADMIN_PERMISSIONS.DASHBOARD_VIEW,
    ADMIN_PERMISSIONS.USERS_VIEW,
    ADMIN_PERMISSIONS.USERS_MANAGE,
    ADMIN_PERMISSIONS.ROLES_ASSIGN,
    ADMIN_PERMISSIONS.AGENCIES_VIEW,
    ADMIN_PERMISSIONS.FEATURES_MANAGE,
    ADMIN_PERMISSIONS.ANALYTICS_VIEW,
    ADMIN_PERMISSIONS.TICKETS_MANAGE,
    ADMIN_PERMISSIONS.PROFILE_VIEW,
    ADMIN_PERMISSIONS.ACTIVITY_VIEW,
    ADMIN_PERMISSIONS.PASSWORD_RESET,
    ADMIN_PERMISSIONS.REPORTS_VIEW,
  ],
  [ADMIN_ROLES.SUPPORT]: [
    ADMIN_PERMISSIONS.DASHBOARD_VIEW,
    ADMIN_PERMISSIONS.USERS_VIEW,
    ADMIN_PERMISSIONS.AGENCIES_VIEW,
    ADMIN_PERMISSIONS.TICKETS_MANAGE,
    ADMIN_PERMISSIONS.PROFILE_VIEW,
    ADMIN_PERMISSIONS.ACTIVITY_VIEW,
  ],
};



/**
 * Phone shape accepted across the admin layer (contact details and the user
 * editor). Deliberately permissive — the app stores international numbers as
 * free text, e.g. "+61 400 111 222".
 */
export const ADMIN_PHONE_PATTERN = /^[+()\d\s-]{6,}$/;

/**
 * The sections of the Audit Log.
 *
 * Login Activity is one of them rather than a module of its own: sign-in
 * attempts are one kind of tracked event, alongside the suspensions, role
 * changes, resets and exports the console also has to answer for. It is the
 * only one with its own **view** — sign-in records answer different questions
 * (where from, which device, which address), so they are read through their own
 * table rather than squeezed into the audit table's columns.
 */
export const AUDIT_CATEGORIES = {
  LOGIN: 'Login Activity',
  USER: 'User Management',
  ROLE: 'Roles & Access',
  FEATURE: 'Feature Sets',
  PASSWORD: 'Password',
  REPORT: 'Reports',
  ADMIN: 'Admin Actions',
  SECURITY: 'Security',
};

/**
 * Order of the category filters on the Audit Log table.
 *
 * `LOGIN` is deliberately absent: Login Activity is reached through its own
 * view, so it is never a chip that reshapes the audit table.
 */
export const AUDIT_CATEGORY_ORDER = [
  AUDIT_CATEGORIES.USER,
  AUDIT_CATEGORIES.ROLE,
  AUDIT_CATEGORIES.FEATURE,
  AUDIT_CATEGORIES.PASSWORD,
  AUDIT_CATEGORIES.REPORT,
  AUDIT_CATEGORIES.ADMIN,
  AUDIT_CATEGORIES.SECURITY,
];

/**
 * Outcomes an audit event can carry. `Success` / `Failed` / `Blocked` are the
 * sign-in vocabulary the log already used; `Sent` covers a dispatched reset.
 * All four already have colours in `STATUS_COLORS`.
 */
export const AUDIT_STATUSES = ['Success', 'Failed', 'Blocked', 'Sent'];

/**
 * Feature set states. A set is never destroyed: deactivating one is a soft
 * delete — the bundle, its features and every role, agency and user holding it
 * are kept exactly as they were, so activating it again restores the grant
 * rather than rebuilding it.
 */
export const FEATURE_SET_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

/** User account states surfaced in the admin user list. */
export const USER_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Suspended',
  INVITED: 'Invited',
};

/**
 * The three closed worlds an access configuration can belong to.
 *
 * Every configuration carries its `roleType`, and nothing crosses between them:
 * an agent configuration can never name a vendor, a vendor configuration can
 * never name an agent, and a solicitor configuration stays on its own until the
 * buyer/vendor solicitor split is settled.
 */
export const ACCESS_ROLE_TYPES = {
  AGENT: 'agent',
  VENDOR: 'vendor',
  SOLICITOR: 'solicitor',
};

/** Section order on Roles & Permissions. */
export const ACCESS_ROLE_TYPE_ORDER = [
  ACCESS_ROLE_TYPES.AGENT,
  ACCESS_ROLE_TYPES.VENDOR,
  ACCESS_ROLE_TYPES.SOLICITOR,
];

/**
 * What each role type may name, and under which field.
 *
 *   shortTitle      the world's name on its own, for "Agent only" style captions
 *   primaryRoles    `primaryUserType` values allowed as the primary account
 *   linkedRoles     `primaryUserType` values allowed as a linked account
 *   primaryField    where the primary account id is stored on a configuration
 *   linkedField     where the linked account ids are stored, `null` if the type
 *                   has no linked step at all
 *   propertyFields  the property fields that tie a property to these accounts
 *
 * These lists are the boundary. `utils/admin.access.utils` normalises every
 * configuration against them before it is stored, so an invalid combination
 * cannot survive even if it is constructed outside the UI.
 *
 * An Agent may be linked under another Agent (Elena Petrova under Nadia
 * Haddad), so `Agent` appears in the agent type's `linkedRoles` too — both are
 * agent-side accounts, which crosses no boundary.
 */
export const ACCESS_ROLE_TYPE_CONFIG = {
  [ACCESS_ROLE_TYPES.AGENT]: {
    id: ACCESS_ROLE_TYPES.AGENT,
    title: 'Agent access',
    shortTitle: 'Agent',
    description:
      'An agent, the linked agents working under them, and the properties they hold.',
    primaryTerm: 'Agent',
    primaryRoles: ['Agent'],
    linkedTerm: 'Linked Agent',
    linkedRoles: ['Agent', 'Linked Agent'],
    primaryField: 'agentId',
    linkedField: 'linkedAgentIds',
    propertyFields: ['agentId'],
  },
  [ACCESS_ROLE_TYPES.VENDOR]: {
    id: ACCESS_ROLE_TYPES.VENDOR,
    title: 'Vendor access',
    shortTitle: 'Vendor',
    description:
      'A primary vendor, the secondary vendors alongside them, and the properties they are selling.',
    primaryTerm: 'Primary Vendor',
    primaryRoles: ['Primary Vendor'],
    linkedTerm: 'Secondary Vendor',
    linkedRoles: ['Secondary Vendor'],
    primaryField: 'primaryVendorId',
    linkedField: 'secondaryVendorIds',
    propertyFields: ['primaryVendorId', 'secondaryVendorId'],
  },
  [ACCESS_ROLE_TYPES.SOLICITOR]: {
    id: ACCESS_ROLE_TYPES.SOLICITOR,
    title: 'Solicitor access',
    shortTitle: 'Solicitor',
    description:
      'A solicitor and the matters they act on. Deliberately flat — the buyer / vendor solicitor split is not settled, so no hierarchy is invented here.',
    primaryTerm: 'Solicitor',
    primaryRoles: ['Solicitor'],
    linkedTerm: null,
    linkedRoles: [],
    primaryField: 'solicitorId',
    linkedField: null,
    propertyFields: ['solicitorId'],
  },
};

export const getAccessRoleType = (roleType) =>
  ACCESS_ROLE_TYPE_CONFIG[roleType] || null;

/** The access role type a `primaryUserType` belongs to, or `null`. */
export const roleTypeForUserRole = (roleName) =>
  ACCESS_ROLE_TYPE_ORDER.find((roleType) => {
    const config = ACCESS_ROLE_TYPE_CONFIG[roleType];
    return (
      config.primaryRoles.includes(roleName) ||
      config.linkedRoles.includes(roleName)
    );
  }) || null;

/** The two levels an access configuration can be saved at. */
export const ACCESS_LEVELS = {
  FULL: 'full',
  PARTIAL: 'partial',
};

/**
 * Lifecycle of an agent → admin support ticket.
 *
 * Parked with the rest of Ticket Management — nothing in the current UI reads
 * these. Kept in place so the archived module restores without touching config.
 */
export const TICKET_STATUS = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

/** How urgently a ticket needs an admin. Set from the ticket list. */
export const TICKET_PRIORITY = {
  HIGH: 'High',
  NORMAL: 'Normal',
  LOW: 'Low',
};

/**
 * Backend capabilities the admin layer needs but which do not exist today.
 * Nothing here is called – it is documentation, not an invented contract.
 *
 * Existing endpoints that WILL be reused once the admin scope is added
 * server-side (see `constant/app.config.js`):
 *   - FORGOT_PASSWORD ('user/forgotPassword')  → admin-initiated password reset
 *   - GETUSER_DETAILS ('/user/getUserDetails') → single user lookup
 *   - PROFILE_SETTING ('user/editProfile')     → contact detail updates
 *   - GET_CONTACT     ('agent/getContact/...') → contact directory
 */
export const ADMIN_API_GAPS = {

  listUsers:
    'No paginated "list all users" endpoint exists (only role-scoped lists).',
  updateUserStatus:
    'No endpoint to activate / deactivate / suspend a user account.',
  assignRole: "No endpoint to change a user's primaryUserType / userType[].",
  featureSets: 'No feature-set or permission entity exists server-side.',
  agencies:
    'No agency entity exists server-side (agencies are free text today).',
  roleAccessScope:
    'No endpoint to scope a role to specific properties / users. Access is ' +
    "currently implicit in each endpoint's own authorisation.",
  analytics:
    'No admin-scoped analytics endpoints (overall, per property or per agent). ' +
    'The agent Insights screens read their own data through the end-user token.',
  tickets:
    'No support-ticket entity or agent↔admin conversation endpoint. The module ' +
    'is parked (see `_archived-modules/admin/README.md`).',
  notifications:
    'No admin notification feed or read-state endpoint. The module is parked ' +
    '(see `_archived-modules/admin/README.md`); its triggers are now recorded ' +
    'as Audit Log events instead.',
  auditLog:
    'No audit-trail endpoint. Suspensions, reactivations, role and feature ' +
    'changes, password resets, report exports and other high-risk admin ' +
    'actions are seeded in `data/admin/admin.audit.data.js`; sign-in events ' +
    'are derived from the same `loginActivity` records the console already had.',
  adminProfile:
    "No endpoint for an administrator's own profile. Edits are written to " +
    'the namespaced admin session only.',
  loginActivity:
    'No login/activity audit endpoint (only lastSeenUser on login).',
  organisationContact: 'No organisation-level contact information endpoint.',
  reportsScope:
    'No admin-scoped property/report endpoints. The Reports module calls the ' +
    'existing agent endpoints (agent/getPropertyList, agent/getIndividualProperty, ' +
    'agent/offerActivity, agent/getMultiplePipeline), which are authorised by the ' +
    "end-user `accesstoken` and return only the signed-in agent's properties. " +
    'Until an admin-scoped equivalent exists, the module requires a valid retreX ' +
    'app session and cannot report across all agents.',
  vendorSolicitor:
    "No agent- or admin-facing endpoint for a property's vendor/buyer solicitor; " +
    'every solicitor/* endpoint is solicitor-scoped.',
};
